import { Rosie } from "./entities/Rosie";
import { DustBunny } from "./entities/DustBunny";
import { BotBrain, type BotPersonality } from "./ai/BotBrain";
import { BOT_ACCENTS, PLAYER_ACCENT, nextBotName } from "./colors";
import { dist2 } from "./types";

export const WORLD_RADIUS = 1100;
const TARGET_DUST_COUNT = 100;
const BOT_COUNT = 9;

/** Robot Mode lasts this many milliseconds. */
const ROBOT_MODE_MS = 5000;
/** First Robot Mode triggers at this score. */
const ROBOT_FIRST_THRESHOLD = 30;
/** Each subsequent threshold needs this much more than the previous tier — plus a bonus per tier. */
const ROBOT_GAP_BASE = 50;
const ROBOT_GAP_TIER_BONUS = 20;

/** Player visual + collision scale: 1.0 at score 0 → 1.2 at score 80, capped. */
const PLAYER_MAX_SCALE = 1.2;
const PLAYER_SCALE_AT = 80;

export interface DeathEvent {
  victim: Rosie;
  /** Whose trail killed them (null = wall / out-of-bounds). */
  killer: Rosie | null;
  /** Where dust will spawn along the corpse — Game.ts may want to spawn FX here. */
  corpseTrail: { x: number; y: number }[];
}

export interface WorldEvents {
  deaths: DeathEvent[];
  pickups: { who: Rosie; count: number }[];
  /** True for the single frame on which the player crossed a Robot Mode milestone. */
  robotModeTriggered: boolean;
}

export class World {
  player: Rosie;
  bots: Rosie[] = [];
  dust: DustBunny[] = [];
  brains = new WeakMap<Rosie, BotBrain>();

  events: WorldEvents = {
    deaths: [],
    pickups: [],
    robotModeTriggered: false,
  };

  /** Score floor for the previous Robot Mode trigger (or 0 before any have fired). */
  prevRobotThreshold = 0;
  /** Score at which the *next* Robot Mode fires. Read by the HUD progress bar. */
  nextRobotThreshold = ROBOT_FIRST_THRESHOLD;
  /** How many Robot Modes the player has unlocked this run. */
  robotTier = 0;
  /**
   * Seconds since this World was constructed. Drives the time-based
   * difficulty ramp in {@link nextBotTier} and {@link farRespawnPoint}
   * so a patient player who hoards dust still faces meaner bots and
   * tighter respawns as the shift goes on.
   */
  elapsedSec = 0;

  constructor() {
    this.player = new Rosie({
      x: 0,
      y: 0,
      heading: 0,
      accent: PLAYER_ACCENT,
      name: "You",
      isPlayer: true,
      trailLength: 5,
      scale: 1.0,
    });

    this.spawnBots();
    this.spawnInitialDust();
  }

  private spawnBots(): void {
    // Initial wave is all "small" tier so the first 20 seconds feel manageable.
    // A couple of the initial bots are hunters so there's threat from the start.
    for (let i = 0; i < BOT_COUNT; i++) {
      const angle = (i / BOT_COUNT) * Math.PI * 2;
      const r = 500 + Math.random() * 350;
      const personality: BotPersonality = i < 2 ? "hunter" : "gatherer";
      const bot = this.makeBot(
        Math.cos(angle) * r,
        Math.sin(angle) * r,
        angle + Math.PI,
        BOT_ACCENTS[i % BOT_ACCENTS.length],
        { scale: 1.0, trailLength: 8 + Math.floor(Math.random() * 8), personality },
      );
      this.bots.push(bot);
      this.brains.set(bot, new BotBrain(personality));
    }
  }

  private spawnInitialDust(): void {
    for (let i = 0; i < TARGET_DUST_COUNT; i++) {
      this.dust.push(this.makeDustInArena());
    }
  }

  private makeDustInArena(): DustBunny {
    const angle = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random()) * (WORLD_RADIUS - 100);
    const roll = Math.random();
    const value = roll < 0.05 ? 8 : roll < 0.3 ? 3 : 1;
    return new DustBunny(Math.cos(angle) * r, Math.sin(angle) * r, value);
  }

  private makeBot(
    x: number,
    y: number,
    heading: number,
    accent: string,
    cfg: { scale: number; trailLength: number; personality: BotPersonality },
  ): Rosie {
    const bot = new Rosie({
      x,
      y,
      heading,
      accent,
      name: nextBotName(),
      scale: cfg.scale,
      trailLength: cfg.trailLength,
    });
    // Faster, sharper base than before. Bigger bots take a small mobility penalty.
    const sluggish = (cfg.scale - 1.0) * 0.4;
    const baseSpeed = cfg.personality === "hunter" ? 165 : 158;
    const baseTurn = cfg.personality === "hunter" ? 3.0 : 2.85;
    bot.speed = baseSpeed * (1 - sluggish * 0.3);
    bot.turnRate = baseTurn * (1 - sluggish);
    return bot;
  }

  /**
   * Replacement bot config — gets meaner as either the player's score climbs
   * OR enough wall-clock time has passed. The time term is the important one:
   * it stops a patient hoarder from keeping the lobby full of tiny gatherer
   * bots forever. Tuning targets the 30s–60s window where the previous
   * version had no real pressure curve.
   */
  private nextBotTier(): {
    scale: number;
    trailLength: number;
    personality: BotPersonality;
  } {
    const s = this.player.score;
    const t = this.elapsedSec;

    // Score-driven pressure (unchanged shape).
    const scoreLarge = Math.max(0, (s - 50) / 120);
    const scoreMedium = Math.max(0, (s - 20) / 80);
    // Time-driven pressure — kicks in just before the mid-game lull.
    //   medium: 0 at 25s → 1.0 at 60s
    //   large:  0 at 60s → 1.0 at 120s
    const timeMedium = Math.max(0, (t - 25) / 35);
    const timeLarge = Math.max(0, (t - 60) / 60);

    // Take the *max* of the two pressures so playing safe doesn't help —
    // either getting fat or just surviving long enough escalates spawns.
    const largeWeight = Math.min(0.45, Math.max(scoreLarge, timeLarge));
    const mediumWeight = Math.min(0.5, Math.max(scoreMedium, timeMedium));

    const roll = Math.random();
    let scale: number;
    let trailLength: number;
    if (roll < largeWeight) {
      scale = 1.3;
      trailLength = 18 + Math.floor(Math.random() * 12);
    } else if (roll < largeWeight + mediumWeight) {
      scale = 1.15;
      trailLength = 12 + Math.floor(Math.random() * 8);
    } else {
      scale = 1.0;
      trailLength = 8 + Math.floor(Math.random() * 8);
    }
    // Hunter chance climbs with both tier *and* elapsed time. By 90s even the
    // smallest replacement bot is hunter ~60% of the time, so a patient player
    // can't farm an empty corner of the arena.
    const baseHunter = scale >= 1.3 ? 0.7 : scale >= 1.15 ? 0.5 : 0.3;
    const timeHunter = Math.min(0.3, Math.max(0, (t - 30) / 60));
    const hunterChance = Math.min(0.95, baseHunter + timeHunter);
    const personality: BotPersonality = Math.random() < hunterChance ? "hunter" : "gatherer";
    return { scale, trailLength, personality };
  }

  update(dt: number): void {
    this.events.deaths = [];
    this.events.pickups = [];
    this.events.robotModeTriggered = false;

    // Wall-clock-ish run timer. dt is the same per-frame value the player
    // experiences, so this matches "shift length" the user actually feels
    // (and stays in step if we ever pause / freeze the world).
    this.elapsedSec += dt;

    const now = performance.now();
    const all = [this.player, ...this.bots];

    // 1. AI thinks.
    for (const b of this.bots) {
      if (!b.alive) continue;
      this.brains.get(b)?.think(b, all, this.dust, dt);
    }

    // 2. Movement.
    this.player.update(dt);
    for (const b of this.bots) b.update(dt);

    // 3. Dust pickups — radius scales with Rosie.
    const prevPlayerScore = this.player.score;
    for (const r of all) {
      if (!r.alive) continue;
      let collected = 0;
      for (let i = this.dust.length - 1; i >= 0; i--) {
        const d = this.dust[i];
        const dd = dist2(r.x, r.y, d.x, d.y);
        const reach = r.effRadius + d.r;
        if (dd < reach * reach) {
          r.score += d.value;
          r.trailLength += d.value;
          this.dust.splice(i, 1);
          collected += d.value;
        }
      }
      if (collected > 0) this.events.pickups.push({ who: r, count: collected });
    }

    // 4. Player progression: scale grows with score; trip Robot Mode on each 25-point tier.
    const t = Math.min(1, this.player.score / PLAYER_SCALE_AT);
    this.player.scale = 1.0 + (PLAYER_MAX_SCALE - 1.0) * t;

    if (this.player.score >= this.nextRobotThreshold && this.player.score > prevPlayerScore) {
      this.player.invincibleUntil = now + ROBOT_MODE_MS;
      this.events.robotModeTriggered = true;
      this.prevRobotThreshold = this.nextRobotThreshold;
      this.robotTier++;
      this.nextRobotThreshold += ROBOT_GAP_BASE + this.robotTier * ROBOT_GAP_TIER_BONUS;
    }

    // 5. Head-vs-trail collisions. Skip if the head is currently invincible.
    for (const r of all) {
      if (!r.alive) continue;
      if (r.isInvincible(now)) continue;
      for (const other of all) {
        if (other === r || !other.alive) continue;
        const trail = other.trail;
        const spacing = 6;
        const visible = Math.min(trail.length, other.trailLength * spacing);
        const reach = r.effRadius + other.trailBagRadius;
        const reach2 = reach * reach;
        for (let i = 8; i < visible; i += 2) {
          const p = trail[i];
          if (!p) continue;
          if (dist2(r.x, r.y, p.x, p.y) < reach2) {
            this.kill(r, other);
            break;
          }
        }
        if (!r.alive) break;
      }
    }

    // 6. Out-of-bounds — wall counts as null killer.
    const r2 = WORLD_RADIUS * WORLD_RADIUS;
    for (const r of all) {
      if (!r.alive) continue;
      if (r.x * r.x + r.y * r.y > r2) this.kill(r, null);
    }

    // 7. Bot respawn — tiered by current player progress.
    for (let i = 0; i < this.bots.length; i++) {
      if (!this.bots[i].alive) {
        const accent = this.bots[i].accent;
        const spawn = this.farRespawnPoint();
        const tier = this.nextBotTier();
        const replacement = this.makeBot(
          spawn.x,
          spawn.y,
          Math.random() * Math.PI * 2,
          accent,
          tier,
        );
        this.bots[i] = replacement;
        this.brains.set(replacement, new BotBrain(tier.personality));
      }
    }

    // 8. Top up dust.
    while (this.dust.length < TARGET_DUST_COUNT) {
      this.dust.push(this.makeDustInArena());
    }
  }

  private kill(r: Rosie, killer: Rosie | null): void {
    if (!r.alive) return;
    r.alive = false;
    if (killer === this.player && r !== this.player) {
      this.player.kills += 1;
    }

    // Corpse dust drops — capped! Previously this loop dumped dust every 4
    // crumbs along the full trail buffer (trailLength × 6 crumbs), so killing
    // a tail-30 bot rained 40+ dust bunnies into the arena. That flooded the
    // floor and made scoring trivial — every kill was a feast.
    //
    // New rule: at most MAX_CORPSE_DROPS dust per death, spaced along the
    // *visible* portion of the trail (trailLength × spacing).
    const MAX_CORPSE_DROPS = 5;
    const spacing = 6;
    const visibleLen = Math.min(r.trail.length, r.trailLength * spacing);
    const dropCount = Math.min(
      MAX_CORPSE_DROPS,
      Math.max(2, Math.floor(r.trailLength / 3)),
    );
    const corpse: { x: number; y: number }[] = [];
    if (visibleLen > 0) {
      const stride = Math.max(1, Math.floor(visibleLen / dropCount));
      for (let i = 0; i < visibleLen && corpse.length < dropCount; i += stride) {
        const p = r.trail[i];
        if (!p) continue;
        corpse.push({ x: p.x, y: p.y });
        this.dust.push(new DustBunny(p.x + jitter(8), p.y + jitter(8), 2));
      }
    }
    this.events.deaths.push({ victim: r, killer, corpseTrail: corpse });
  }

  private farRespawnPoint(): { x: number; y: number } {
    // The minimum gap between the player and a fresh bot starts at 420 units
    // but shrinks ~4 units per second after the 40s mark, floored at 220.
    // That's still well outside the player's screen (camera follows ~250
    // ahead), but close enough that new threats can converge on a hoarder
    // within seconds instead of having to traverse half the arena.
    const minDist = Math.max(
      220,
      420 - Math.max(0, this.elapsedSec - 40) * 4,
    );
    const minDist2 = minDist * minDist;
    for (let attempt = 0; attempt < 20; attempt++) {
      const angle = Math.random() * Math.PI * 2;
      const r = 500 + Math.random() * 400;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      const dd = dist2(x, y, this.player.x, this.player.y);
      if (dd > minDist2) return { x, y };
    }
    return { x: WORLD_RADIUS * 0.6, y: 0 };
  }
}

function jitter(amount: number): number {
  return (Math.random() - 0.5) * 2 * amount;
}
